/**
 * Regex pattern that matches a full @-mention token: {{@personId}}
 * Use with global flag for multiple matches.
 * Example: "Hello {{@abc123}}" matches "{{@abc123}}"
 */
export const MENTION_TOKEN_PATTERN = /\{\{@[^}]+\}\}/g;

/**
 * Regex pattern that matches a full @-mention token and captures the personId.
 * Use with global flag for multiple matches.
 * Example: "Hello {{@abc123}}".match(MENTION_TOKEN_WITH_ID_PATTERN) captures "abc123"
 */
export const MENTION_TOKEN_WITH_ID_PATTERN = /\{\{@([^}]+)\}\}/g;

/**
 * Creates a regex pattern that matches a specific person's @-mention token.
 * @param personId - The ID of the person to match
 * @returns A global RegExp that matches {{@personId}}
 */
export function createMentionPatternForPerson(personId: string): RegExp {
  return new RegExp(`\\{\\{@${personId}\\}\\}`, 'g');
}

/**
 * Result of an @-mention assignment operation.
 */
type AssignPersonMentionResult = {
  /** The new text value with the mention replaced. */
  newValue: string;
  /** The new HTML content with styled mention. */
  newHtml: string;
};

/**
 * Parameters for the assignPersonMention helper.
 */
type AssignPersonMentionParams = {
  /** Current text value of the input. */
  value: string;
  /** Current cursor position in the text. */
  cursorPosition: number;
  /** The person being mentioned. */
  match: { personId: string; name: string };
  /** List of collaborators with their search keys. */
  collaborators: {
    personId: string;
    name: string | null;
    searchKey: string;
    exactSearchKey: string;
  }[];
  /** Current HTML content of the contenteditable element. */
  currentHtml: string;
};

/**
 * Handles the replacement of an @-mention with a styled collaborator name.
 *
 * This helper computes the new text value and HTML content when a user selects
 * a collaborator from the @-mention dropdown. It finds the most recent @ symbol
 * before the cursor and replaces the partial mention with the full collaborator name.
 *
 * @returns The new value and HTML, or null if no valid @ symbol was found.
 */
export function assignPersonMention({
  value,
  cursorPosition,
  match,
  collaborators,
  currentHtml,
}: AssignPersonMentionParams): AssignPersonMentionResult | null {
  const latestAtIndex = value.substring(0, cursorPosition).lastIndexOf('@');

  if (latestAtIndex === -1) {
    return null;
  }

  const collaborator = collaborators.find((collab) => collab.personId === match.personId);

  if (!collaborator || !collaborator.name) {
    return null;
  }

  // Count how many @ symbols appear before the one we're replacing
  let atNumber = 0;
  let replaceTextStart = 0;
  for (let index = 0; index < latestAtIndex + 1; index += 1) {
    if (value.charAt(index) === '@') {
      atNumber += 1;
      replaceTextStart = index;
    }
  }

  const textToReplace = value.substring(latestAtIndex, cursorPosition);
  const newValue = `${value.substring(0, replaceTextStart)}${value
    .substring(replaceTextStart)
    .replace(textToReplace, `@${collaborator.name}`)}`;

  // Find the corresponding @ in the HTML and replace it with styled span
  let currentHtmlAtNumber = 0;
  let replaceHtmlStart = 0;
  for (let index = 0; index < currentHtml.length; index += 1) {
    if (currentHtml.charAt(index) === '@') {
      currentHtmlAtNumber += 1;
      if (currentHtmlAtNumber === atNumber) {
        replaceHtmlStart = index;
        break;
      }
    }
  }

  const newHtml = `${currentHtml.substring(0, replaceHtmlStart)}${currentHtml
    .substring(replaceHtmlStart)
    .replace(textToReplace, `<span style="color: var(--color-noodle);">@${collaborator.name}</span>&nbsp;`)}`;

  return { newHtml, newValue };
}

/**
 * Moves the cursor to the end of a contenteditable element.
 */
export function moveCursorToEnd(element: HTMLElement): void {
  const range = document.createRange?.();
  if (range) {
    range.selectNodeContents(element);
    range.collapse(false);
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }
}
